import { useEffect, useState } from "react";

import {
  delay,
  replace,
  resolvePromiseWithDelay,
} from "@smart/itops-utils-basic";

import {
  getLocalSmokeballAppContext,
  observeChange,
  publishChange,
  setLocalSmokeballAppContext,
} from "./utils";
import {
  LayoutMatter,
  LayoutMatterItem,
  LocalSmokeballAppContext,
  UpdateLayoutMatterItemRequest,
  UseLocalApiOptions,
} from "../types";

export const useLayouts = ({ context, observe }: UseLocalApiOptions) => {
  const findLocalLayoutMatter = (matterId?: string) =>
    getLocalSmokeballAppContext()?.layoutMatters?.find(
      (lm) => lm.matterId === matterId,
    );

  const [currentLayouts, setCurrentLayouts] = useState<LayoutMatterItem[]>();
  const shouldObserve = observe?.includes("layouts");

  useEffect(() => {
    if (shouldObserve) {
      setCurrentLayouts(findLocalLayoutMatter(context.matterId)?.value);
      observeChange("layouts", () =>
        setCurrentLayouts(findLocalLayoutMatter(context.matterId)?.value),
      );
    }
  }, [context, shouldObserve]);

  return {
    get: (matterId?: string) => {
      const layoutMatter = findLocalLayoutMatter(matterId || context.matterId);
      return resolvePromiseWithDelay(layoutMatter as LayoutMatter);
    },
    update: async ({
      matterId,
      itemId,
      layoutId,
      values,
    }: UpdateLayoutMatterItemRequest) => {
      const matterIdToUse = matterId || context.matterId;
      const layoutMatter = findLocalLayoutMatter(matterId || context.matterId);

      const updatedItems =
        layoutMatter?.value &&
        replace(
          layoutMatter.value,
          (item) => {
            if (itemId) return item.itemId === itemId;
            return item.layoutDesign.id === layoutId;
          },
          (item) => ({ ...item, values }),
        );
      const updatedLayout =
        updatedItems?.find((l) => l.itemId === itemId) ||
        updatedItems?.find((l) => l.layoutDesign.id === layoutId);
      const failedUpdates = values.filter(({ key, value }) => {
        const updatedValue = updatedLayout?.values.find(
          (updated) => updated.key === key || updated.key.includes(key),
        )?.value;
        return (
          (updatedValue ?? "").toLowerCase() !== (value ?? "").toLowerCase()
        );
      });
      if (failedUpdates.length) {
        throw new Error(
          `Fields did not update - ${failedUpdates
            .map((u) => u.key)
            .join(", ")}`,
        );
      }

      const updatedLayoutMatter = {
        ...layoutMatter,
        value: updatedItems,
      } as LayoutMatter;
      const localContext = getLocalSmokeballAppContext();
      const updatedContext = {
        ...localContext,
        layoutMatters:
          localContext?.layoutMatters &&
          replace(
            localContext.layoutMatters,
            (lm) => lm.matterId === matterIdToUse,
            updatedLayoutMatter,
          ),
      } as LocalSmokeballAppContext;
      setLocalSmokeballAppContext(updatedContext);
      publishChange("layouts");

      await delay(0);
      return updatedLayoutMatter;
    },
    getContacts: () => Promise.resolve({ layoutContacts: [] }),
    getLayoutContactsUsingRoleAndRelationship: () =>
      Promise.resolve({ layoutContacts: [] }),
    current: currentLayouts,
  };
};
